import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Aggrid from "../../framework/AgGrid";
import Button from "../../framework/Button";
import NumberInput from "../../framework/NumberInput";
import CellNumberEditor from "../../framework/cellEditor/CellNumberEditor";
import CellTextEditor from "../../framework/cellEditor/CellTextEditor";
import GlobalContext from "../../framework/GlobalContext";
import Shortcut from "../../framework/Shortcut";
import SkeletonGrid from "../../framework/skeleton/SkeletonGrid";
import { addModeConfig } from "../../services/mode_config-services";
import {
  cellTabbed,
  debounceFocusCell,
  focusCell,
  rowDown,
  rowUp,
  selectRow,
  stopEditing,
} from "../../utils/gridUtils";
import { getStringValue, getIncentiveCategory, isZero } from "../../utils/utils";
import classes from "./ModeConfigPricingUpdateAutocomplete.module.css";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import { displayToast } from "../../framework/toast/toastUtils";

const ModeConfigPricingUpdateAutocomplete = (props) => {
  const { onEscape = () => {}, rowData, onSave = () => {} } = props;
  const [invoiceDetailsRowData, setInvoiceDetailsRowData] = useState(null);
  const [bulkPrice, setBulkPrice] = useState("");
  const [bulkGst, setBulkGst] = useState("");
  const [api, setApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);
  const saveRef = useRef();
  const { setCurrentComponent, currentComponent, setShowLoading } =
    useContext(GlobalContext);

  const defaultColDef = {
    cellStyle: {
      color: "black",
      fontSize: "13px",
      fontWeight: 400,
    },
    resizable: true,
    suppressKeyboardEvent: (params) => true,
  };

  const columnDefs = [
    {
      headerName: "Mode Config Id",
      field: "modeConfigId",
      flex: 2,
    },
    {
      headerName: "Group Name",
      field: "groupName",
      flex: 2,
    },
    {
      headerName: "Dosage Mark",
      field: "dosageMark",
      flex: 1,
    },
    {
      headerName: "Description",
      field: "description",
      flex: 1,
      cellClass: "custom-ag-cell-numeric",
    },
    {
      headerName: "Mode Category",
      field: "modeCategory",
      flex: 1,
    },
    {
      headerName: "Potency",
      field: "potency",
      locked: false,
      width: 200,
    },
    {
      headerName: "Packaging",
      field: "packaging",
      locked: false,
      cellClass: "custom-ag-cell-numeric",
      width: 100,
    },
    {
      headerName: "Price",
      field: "rate",
      cellClass: "custom-ag-cell-numeric",
      cellEditor: "cellNumberEditor",
      editable: true,
      cellEditorParams: (params) => {
        return {
          value: getStringValue(params.data.rate),
          decimalScale: 2,
          onChange: (values, e) => {
            let changedNodeValue = params.node;
            changedNodeValue.data.rate = values.floatValue;
            cellValueChanged(changedNodeValue);
          },
        };
      },
      flex: 1,
      cellRendererFramework: (params) => {
        return params.colDef.editable ? (
          <div className="ag-editable-cell">
            {getStringValue(params.data.rate)}
          </div>
        ) : (
          getStringValue(params.data.rate)
        );
      },
    },
    {
      headerName: "GST %",
      field: "gstPercent",
      editable: true,
      cellEditor: "cellTextEditor",
      flex: 1,
      onCellValueChanged: (params) => cellValueChanged(params.node),
      cellEditorParams: (params) => {
        return {
          value: getStringValue(params.data.gstPercent),
          uppercase: true,
        };
      },
      cellRendererFramework: (params) => {
        return params.colDef.editable ? (
          <div className="ag-editable-cell">
            {getStringValue(params.data.gstPercent)}
          </div>
        ) : (
          getStringValue(params.data.gstPercent)
        );
      },
    },
    {
      headerName: "DP %",
      field: "dpPercentage",
      editable: true,
      cellEditor: "cellTextEditor",
      flex: 1,
      onCellValueChanged: (params) => cellValueChanged(params.node),
      cellEditorParams: (params) => {
        return {
          value: getStringValue(params.data.dpPercentage),
          uppercase: true,
        };
      },
      cellRendererFramework: (params) => {
        return params.colDef.editable ? (
          <div className="ag-editable-cell">
            {getStringValue(params.data.dpPercentage)}
          </div>
        ) : (
          getStringValue(params.data.dpPercentage)
        );
      },
    },
  ];

  const mainGridMarkers = useMemo(() => {
    return {
      altDown: "grid_set_rack",
      altUp: "grid_set_rack",
      left: "grid_set_rack",
      up: "grid_set_rack",
      right: "grid_set_rack",
      down: "grid_set_rack",
    };
  }, []);

  const gridEditingStopped = useCallback(
    (direction) => {
      if (direction && mainGridMarkers[direction]) {
        document
          .getElementById(mainGridMarkers[direction])
          .focus({ preventScroll: true });
        setCurrentComponent(mainGridMarkers[direction]);
      }
      if (api) stopEditing(api);
    },
    [api, mainGridMarkers, setCurrentComponent]
  );

  const tabHandler = useCallback(
    (shiftKey) => {
      if (!api) return;
      const currentFocusedCell = api.getFocusedCell();
      if (!cellTabbed(api, shiftKey) && currentFocusedCell) {
        focusCell(api, currentFocusedCell.rowIndex, currentFocusedCell.column);
      }
    },
    [api]
  );

  const applyBulkPriceGst = useCallback(() => {
    setInvoiceDetailsRowData((prevRows) => {
      if (!prevRows?.length) {
        return prevRows;
      }
      return prevRows.map((row) => ({
        ...row,
        rate:
          bulkPrice !== "" && bulkPrice !== null ? Number(bulkPrice) : row.rate,
        gstPercent:
          bulkGst !== "" && bulkGst !== null ? Number(bulkGst) : row.gstPercent,
      }));
    });
  }, [bulkGst, bulkPrice]);

  const saveHandler = useCallback(async () => {
    gridEditingStopped();
    setShowLoading(true);
    let data = invoiceDetailsRowData.map((med) => {
      return {
        _id: med._id,
        modeConfigId: med.modeConfigId,
        rate: med?.rate,
        gstPercent: med?.gstPercent,
        dpPercentage: med?.dpPercentage,
        incentiveCategory: getIncentiveCategory(med?.groupName),
      };
    });
    await addModeConfig({ data }).then((resp) => {
      if (resp?.status === "success") {
        displayToast({
          type: "success",
          title: "Mode config pricing details saved successfully",
        });
        setShowLoading(false);
        onSave(resp.data);
      }
    });
  }, [gridEditingStopped, invoiceDetailsRowData, onSave, setShowLoading]);

  const cellValueChanged = useCallback(
    (node) => {
      let updatedRows = [...invoiceDetailsRowData];
      updatedRows[node.rowIndex] = node.data;
      setInvoiceDetailsRowData([...updatedRows]);
    },
    [invoiceDetailsRowData]
  );

  const keyupListener = useCallback(
    (e) => {
      if (!api) return;
      let key = e.key?.toLowerCase();
      let ctrlKey = e.ctrlKey;
      let shiftKey = e.shiftKey;
      let altKey = e.altKey;
      if (key === "tab") {
        tabHandler(shiftKey);
        return;
      }
      if (key === "enter") {
        tabHandler(shiftKey);
        return;
      }
      if (key === "arrowdown") {
        if (altKey) {
          // gridEditingStopped("altDown");
          return;
        }
        if (api.getFocusedCell().column.colDef.editable) {
          if (!rowDown(api, ctrlKey, api.getFocusedCell().column)) {
            // gridEditingStopped("down");
          }
          return;
        }
      }
      if (key === "arrowup") {
        if (altKey) {
          // gridEditingStopped("altUp");
          return;
        }
        if (api.getFocusedCell().column.colDef.editable) {
          if (!rowUp(api, ctrlKey, api.getFocusedCell().column)) {
            // gridEditingStopped("up");
          }
          return;
        }
      }
      if (key === "escape") {
        return onEscape();
      }
      if (ctrlKey && key === "s") {
        gridEditingStopped();
        saveRef.current.click();
        return;
      }
    },
    [api, gridEditingStopped, onEscape, tabHandler]
  );
  useEffect(() => {
    setCurrentComponent("grid_set_rack");
  }, [setCurrentComponent]);

  useEffect(() => {
    if (rowData) {
      setInvoiceDetailsRowData([...rowData]);
    }
    return () => {
      setInvoiceDetailsRowData(null);
    };
  }, [rowData]);

  useEffect(() => {
    if (api && columnApi && invoiceDetailsRowData?.length > 0) {
      selectRow(api, 0);
      debounceFocusCell(api, 0, columnApi.getColumn("rate"));
    }
  }, [api, columnApi]);

  useEffect(() => {
    if (
      currentComponent !== "grid_set_rack" &&
      api &&
      api.getSelectedNodes()?.length
    ) {
      gridEditingStopped();
    }
  }, [api, currentComponent, gridEditingStopped]);
  useEffect(() => {
    if (
      ["grid_set_rack", "bulk_price_input", "bulk_gst_input"].includes(
        currentComponent
      )
    ) {
      document.addEventListener("keyup", keyupListener, false);
    }
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [currentComponent, keyupListener]);
  return (
    <>
      <div
        className={`${classes["mode-config-pricing-update-autocomplete-container"]}`}
      >
        <div
          className={classes["mode-config-pricing-update-autocomplete-header"]}
        >
          <div
            className={classes["mode-config-pricing-update-autocomplete-title"]}
          >
            Set Details
          </div>
          <div
            className={
              classes[
                "mode-config-pricing-update-autocomplete-esc-shortcut-container"
              ]
            }
          >
            <Shortcut keys={["Esc"]} label="Go Back" />
          </div>
        </div>
        <div style={{ display: "flex", height: "calc(100% - 100px)" }}>
          <div
            className={
              classes["mode-config-pricing-update-autocomplete-search-table"]
            }
          >
            {!invoiceDetailsRowData && <SkeletonGrid />}
            {invoiceDetailsRowData && (
              <Aggrid
                id="grid_set_rack"
                rowData={invoiceDetailsRowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                onGridReady={(params) => {
                  setApi(params.api);
                  setColumnApi(params.columnApi);
                }}
                onRowDataChanged={(params) => {
                  selectRow(params.api, 0);
                  debounceFocusCell(
                    params.api,
                    0,
                    params.columnApi.getColumn("rate")
                  );
                }}
                frameworkComponents={{
                  cellTextEditor: CellTextEditor,
                  cellNumberEditor: CellNumberEditor,
                }}
              />
            )}
          </div>
        </div>
        <div className={`${classes["footer"]}`}>
          <div
            className={
              classes["mode-config-pricing-update-autocomplete-footer-total"]
            }
          >
            <FilterGroup>
              <FilterItem>
                <NumberInput
                  id="bulk_price_input"
                  label="Price"
                  value={bulkPrice}
                  allowNegative={false}
                  onFocus={() => setCurrentComponent("bulk_price_input")}
                  onValueChange={(values) =>
                    setBulkPrice(values.floatValue ?? "")
                  }
                  decimalScale={2}
                  placeholder="Enter price"
                  inputProps={{
                    "data-markers": JSON.stringify({
                      up: "grid_set_rack",
                      down: "bulk_price_input",
                      left: "bulk_price_input",
                      right: "bulk_gst_input",
                    }),
                  }}
                />
              </FilterItem>
              <FilterItem>
                <NumberInput
                  id="bulk_gst_input"
                  label="GST %"
                  value={bulkGst}
                  allowNegative={false}
                  onFocus={() => setCurrentComponent("bulk_gst_input")}
                  onValueChange={(values) =>
                    setBulkGst(values.floatValue ?? "")
                  }
                  decimalScale={2}
                  placeholder="Enter GST %"
                  inputProps={{
                    "data-markers": JSON.stringify({
                      up: "grid_set_rack",
                      down: "bulk_gst_input",
                      left: "bulk_price_input",
                      right: "bulk_apply_button",
                    }),
                  }}
                />
              </FilterItem>
              <FilterItem>
                <Button
                  id="bulk_apply_button"
                  label="Apply"
                  onClick={applyBulkPriceGst}
                  onFocus={() => setCurrentComponent("bulk_apply_button")}
                  markers={JSON.stringify({
                    up: "grid_set_rack",
                    down: "bulk_apply_button",
                    left: "bulk_gst_input",
                    right: "mode-config-pricing-update-autocomplete-save",
                  })}
                />
              </FilterItem>
            </FilterGroup>
          </div>
          <div
            className={
              classes["mode-config-pricing-update-autocomplete-footer-button"]
            }
          >
            <Button
              label="Save"
              id="mode-config-pricing-update-autocomplete-save"
              ref={saveRef}
              endIcon={<Shortcut keys={["Ctrl", "S"]} />}
              onFocus={() =>
                setCurrentComponent(
                  "mode-config-pricing-update-autocomplete-save"
                )
              }
              markers={JSON.stringify({
                up: "grid_set_rack",
                down: "mode-config-pricing-update-autocomplete-save",
                left: "bulk_apply_button",
                right: "mode-config-pricing-update-autocomplete-save",
              })}
              onClick={() => {
                saveHandler();
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ModeConfigPricingUpdateAutocomplete;
